import { useEffect, useState } from 'react';
import { Modal, Avatar, Spin, Tag, Divider, Tabs, Empty } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, CrownOutlined, ProjectOutlined, TeamOutlined, SafetyOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { ProfileAPIs } from '~/common/typescript/apis/ProfileAPIs';
import { User_Base } from '~/common/typescript/models/User';
import { useAtom } from 'jotai';
import { userProfileModalAtom } from './UserProfileStore';
import { API_URL_ADMIN } from '~/config/config';
import { formatDate } from '~/utils/dateUtils';

const { TabPane } = Tabs;

const UserProfile = () => {
  const [modalState, setModalState] = useAtom(userProfileModalAtom);
  const [user, setUser] = useState<User_Base | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (modalState.visible && modalState.userId) {
      fetchUserProfile(modalState.userId);
    }
  }, [modalState.visible, modalState.userId]);

  const fetchUserProfile = async (userId: number) => {
    setLoading(true);
    try {
      const userData = await ProfileAPIs.remote_getProfile(userId);
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setModalState({ visible: false, userId: null });
    setUser(null);
  };

  const getAvatarUrl = () => {
    if (user?.avatar && typeof user.avatar === 'object' && 'url' in user.avatar) {
      return `${API_URL_ADMIN}${user.avatar.url}`;
    }
    return undefined;
  };

  const getTierColor = (tier?: string) => {
    const colorMap: { [key: string]: string } = {
      BASIC: 'default',
      PRO: 'gold',
      PREMIUM: 'purple',
    };
    return colorMap[tier || 'BASIC'] || 'default';
  };

  return (
    <Modal
      open={modalState.visible}
      onCancel={handleClose}
      footer={null}
      width={700}
      centered
      bodyStyle={{ padding: '16px' }}
    >
      <Spin spinning={loading}>
        {user && (
          <Container>
            <Header>
              <Avatar
                size={80}
                src={getAvatarUrl()}
                icon={<UserOutlined />}
                style={{ border: '3px solid #3b82f6' }}
              />
              <UserInfo>
                <UserName>{user.fullname || user.username}</UserName>
                <UserUsername>@{user.username}</UserUsername>
                <TierRow>
                  {user.gcTier && (
                    <Tag color={getTierColor(user.gcTier)} icon={<CrownOutlined />}>
                      GC {user.gcTier}
                    </Tag>
                  )}
                  {user.fpTier && (
                    <Tag color={getTierColor(user.fpTier)}>
                      FP {user.fpTier}
                    </Tag>
                  )}
                  {user.gdTier && (
                    <Tag color={getTierColor(user.gdTier)}>
                      GD {user.gdTier}
                    </Tag>
                  )}
                </TierRow>
              </UserInfo>
            </Header>

            <Divider style={{ margin: '16px 0 12px 0' }} />

            <Tabs defaultActiveKey="overview" size="small">
              <TabPane tab={<TabTitle><UserOutlined /> Tổng quan</TabTitle>} key="overview">
                <TabContent>
                  <InfoSection>
                    {user.email && (
                      <InfoRow>
                        <InfoIcon><MailOutlined /></InfoIcon>
                        <InfoContent>
                          <InfoLabel>Email</InfoLabel>
                          <InfoValue>{user.email}</InfoValue>
                        </InfoContent>
                      </InfoRow>
                    )}

                    {user.phoneNumber && (
                      <InfoRow>
                        <InfoIcon><PhoneOutlined /></InfoIcon>
                        <InfoContent>
                          <InfoLabel>Số điện thoại</InfoLabel>
                          <InfoValue>{user.phoneNumber}</InfoValue>
                        </InfoContent>
                      </InfoRow>
                    )}

                    {user.job_title && (
                      <InfoRow>
                        <InfoIcon><TeamOutlined /></InfoIcon>
                        <InfoContent>
                          <InfoLabel>Chức vụ</InfoLabel>
                          <InfoValue>{user.job_title}</InfoValue>
                        </InfoContent>
                      </InfoRow>
                    )}

                    {user.department && (
                      <InfoRow>
                        <InfoIcon><TeamOutlined /></InfoIcon>
                        <InfoContent>
                          <InfoLabel>Phòng ban</InfoLabel>
                          <InfoValue>{user.department}</InfoValue>
                        </InfoContent>
                      </InfoRow>
                    )}

                    {user.birthday_day && user.birthday_month && user.birthday_year && (
                      <InfoRow>
                        <InfoIcon><CalendarOutlined /></InfoIcon>
                        <InfoContent>
                          <InfoLabel>Ngày sinh</InfoLabel>
                          <InfoValue>
                            {user.birthday_day}/{user.birthday_month}/{user.birthday_year}
                          </InfoValue>
                        </InfoContent>
                      </InfoRow>
                    )}
                  </InfoSection>

                  {user.aboutMe && (
                    <>
                      <Divider style={{ margin: '12px 0' }} />
                      <AboutSection>
                        <AboutLabel>Giới thiệu</AboutLabel>
                        <AboutText>{user.aboutMe}</AboutText>
                      </AboutSection>
                    </>
                  )}
                </TabContent>
              </TabPane>

              <TabPane tab={<TabTitle><CrownOutlined /> Gói dịch vụ</TabTitle>} key="subscription">
                <TabContent>
                  <SubscriptionSection>
                    <SubscriptionGrid>
                      {user.gcExpireAt && (
                        <SubscriptionCard>
                          <SubscriptionCardHeader>
                            <SubscriptionCardTitle>Geocollect</SubscriptionCardTitle>
                            <Tag color={getTierColor(user.gcTier)}>{user.gcTier || 'BASIC'}</Tag>
                          </SubscriptionCardHeader>
                          <SubscriptionCardItem>
                            <SubscriptionCardLabel>Hết hạn:</SubscriptionCardLabel>
                            <SubscriptionCardValue>
                              {formatDate(user.gcExpireAt)}
                            </SubscriptionCardValue>
                          </SubscriptionCardItem>
                          {(user as any).gcMaxNDevices && (
                            <SubscriptionCardItem>
                              <SubscriptionCardLabel>Số thiết bị:</SubscriptionCardLabel>
                              <SubscriptionCardValue>{(user as any).gcMaxNDevices}</SubscriptionCardValue>
                            </SubscriptionCardItem>
                          )}
                        </SubscriptionCard>
                      )}

                      {user.fpExpireAt && (
                        <SubscriptionCard>
                          <SubscriptionCardHeader>
                            <SubscriptionCardTitle>Flight Planner</SubscriptionCardTitle>
                            <Tag color={getTierColor(user.fpTier)}>{user.fpTier || 'BASIC'}</Tag>
                          </SubscriptionCardHeader>
                          <SubscriptionCardItem>
                            <SubscriptionCardLabel>Hết hạn:</SubscriptionCardLabel>
                            <SubscriptionCardValue>
                              {formatDate(user.fpExpireAt)}
                            </SubscriptionCardValue>
                          </SubscriptionCardItem>
                          {(user as any).fpMaxNDevices && (
                            <SubscriptionCardItem>
                              <SubscriptionCardLabel>Số thiết bị:</SubscriptionCardLabel>
                              <SubscriptionCardValue>{(user as any).fpMaxNDevices}</SubscriptionCardValue>
                            </SubscriptionCardItem>
                          )}
                        </SubscriptionCard>
                      )}

                      {user.gdExpireAt && (
                        <SubscriptionCard>
                          <SubscriptionCardHeader>
                            <SubscriptionCardTitle>Geodrone</SubscriptionCardTitle>
                            <Tag color={getTierColor(user.gdTier)}>{user.gdTier || 'BASIC'}</Tag>
                          </SubscriptionCardHeader>
                          <SubscriptionCardItem>
                            <SubscriptionCardLabel>Hết hạn:</SubscriptionCardLabel>
                            <SubscriptionCardValue>
                              {formatDate(user.gdExpireAt)}
                            </SubscriptionCardValue>
                          </SubscriptionCardItem>
                          {(user as any).gdMaxNDevices && (
                            <SubscriptionCardItem>
                              <SubscriptionCardLabel>Số thiết bị:</SubscriptionCardLabel>
                              <SubscriptionCardValue>{(user as any).gdMaxNDevices}</SubscriptionCardValue>
                            </SubscriptionCardItem>
                          )}
                        </SubscriptionCard>
                      )}
                    </SubscriptionGrid>
                  </SubscriptionSection>
                </TabContent>
              </TabPane>

              <TabPane tab={<TabTitle><ProjectOutlined /> Dự án ({(user as any).projects?.length || 0})</TabTitle>} key="projects">
                <TabContent>
                  {(user as any).projects && (user as any).projects.length > 0 ? (
                    <ProjectsList>
                      {(user as any).projects
                        .filter((project: any) => !project.is_deleted)
                        .map((project: any, index: number) => (
                          <ProjectCard key={project.id || index}>
                            <ProjectHeader>
                              <ProjectIcon><ProjectOutlined /></ProjectIcon>
                              <div>
                                <ProjectName>{project.name}</ProjectName>
                                <ProjectMeta>
                                  <Tag color="blue" style={{ fontSize: '10px' }}>{project.type}</Tag>
                                  <ProjectDate>
                                    {formatDate(project.createdAt)}
                                  </ProjectDate>
                                </ProjectMeta>
                              </div>
                            </ProjectHeader>
                            {project.description && (
                              <ProjectDescription>{project.description}</ProjectDescription>
                            )}
                          </ProjectCard>
                        ))}
                    </ProjectsList>
                  ) : (
                    <Empty description="Chưa có dự án nào" />
                  )}
                </TabContent>
              </TabPane>

              <TabPane tab={<TabTitle><SafetyOutlined /> Vai trò</TabTitle>} key="role">
                <TabContent>
                  <InfoSection>
                    {(user as any).role && (
                      <InfoRow>
                        <InfoIcon><SafetyOutlined /></InfoIcon>
                        <InfoContent>
                          <InfoLabel>Vai trò hệ thống</InfoLabel>
                          <InfoValue>
                            {(user as any).role.name}
                            {(user as any).role.description && (
                              <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginTop: '4px' }}>
                                {(user as any).role.description}
                              </span>
                            )}
                          </InfoValue>
                        </InfoContent>
                      </InfoRow>
                    )}

                    {(user as any).workspaces && (user as any).workspaces.length > 0 && (
                      <>
                        <Divider style={{ margin: '12px 0' }} />
                        <AboutSection>
                          <AboutLabel>Workspace</AboutLabel>
                          {(user as any).workspaces.map((workspace: any, index: number) => (
                            <WorkspaceItem key={workspace.id || index}>
                              <WorkspaceName>{workspace.name}</WorkspaceName>
                              {workspace.description && (
                                <WorkspaceDescription>{workspace.description}</WorkspaceDescription>
                              )}
                            </WorkspaceItem>
                          ))}
                        </AboutSection>
                      </>
                    )}
                  </InfoSection>
                </TabContent>
              </TabPane>
            </Tabs>
          </Container>
        )}
      </Spin>
    </Modal>
  );
};

export default UserProfile;

const Container = styled.div`
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 4px;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const UserUsername = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
`;

const TierRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const TabTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
`;

const TabContent = styled.div`
  padding: 12px 4px;
  max-height: 400px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InfoIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 8px;
  color: #3b82f6;
  font-size: 16px;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #334155;
  font-weight: 500;
`;

const AboutSection = styled.div``;

const AboutLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  font-weight: 600;
`;

const AboutText = styled.div`
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
`;

const SubscriptionSection = styled.div``;

const SubscriptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const SubscriptionCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const SubscriptionCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SubscriptionCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const SubscriptionCardItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-top: 1px solid #e2e8f0;

  &:first-of-type {
    border-top: none;
  }
`;

const SubscriptionCardLabel = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const SubscriptionCardValue = styled.div`
  font-size: 13px;
  color: #334155;
  font-weight: 600;
`;

const ProjectsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProjectCard = styled.div`
  background: #ffffff;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const ProjectIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
`;

const ProjectName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProjectDate = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

const ProjectDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 8px;
  padding-left: 42px;
  line-height: 1.5;
`;

const WorkspaceItem = styled.div`
  padding: 10px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const WorkspaceName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const WorkspaceDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;
